import { useCallback, useMemo, useState } from 'react';

export interface UseNodeCollapseReturn {
  /** True when the given node is currently rendered as collapsed. */
  readonly isCollapsed: (nodeId: string) => boolean;
  /** Flip a single node's collapsed state. */
  readonly toggleCollapsed: (nodeId: string) => void;
  /**
   * Force every supplied node id into the same collapsed state. Powers
   * the bottom-toolbar's Collapse-all / Expand-all controls. Passing a
   * subset is allowed -- only listed ids are touched.
   */
  readonly setAllCollapsed: (
    nodeIds: readonly string[],
    collapsed: boolean,
  ) => void;
}

/**
 * UI-only collapsed state for canvas nodes. The flag never round-trips
 * to the backend (a node card being visually collapsed has no impact on
 * the persisted graph), so we store it in a plain {@code Set<string>}
 * keyed by node id. Each renderer calls {@link UseNodeCollapseReturn.isCollapsed}
 * to decide whether to render its body fields or just the header.
 *
 * The whole API is identity-stable so consumers in {@code EditorContext}
 * memoising on the function references don't tear unnecessarily.
 */
export function useNodeCollapse(): UseNodeCollapseReturn {
  const [collapsedIds, setCollapsedIds] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );

  const isCollapsed = useCallback(
    (nodeId: string) => collapsedIds.has(nodeId),
    [collapsedIds],
  );

  const toggleCollapsed = useCallback((nodeId: string) => {
    setCollapsedIds((curr) => {
      const next = new Set(curr);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  }, []);

  const setAllCollapsed = useCallback(
    (nodeIds: readonly string[], collapsed: boolean) => {
      setCollapsedIds((curr) => {
        const next = new Set(curr);
        if (collapsed) nodeIds.forEach((id) => next.add(id));
        else nodeIds.forEach((id) => next.delete(id));
        return next;
      });
    },
    [],
  );

  return useMemo(
    () => ({ isCollapsed, toggleCollapsed, setAllCollapsed }),
    [isCollapsed, toggleCollapsed, setAllCollapsed],
  );
}
